<template>
  <div class="instruction-packages-list">
    <div class="header">
      <div class="list-title">
        <editor-module-tabs />
      </div>

      <div class="right-section">
        <div class="search">
          <form @submit.prevent="fetchRows">
            <b-field>
              <b-input
                v-model="search"
                :placeholder="$t('search')"
                type="search"
                icon="magnify"
                icon-clickable
                @icon-click="fetchRows"
                class="search-input"
              />
            </b-field>
          </form>
        </div>

        <b-button class="wi-add-button" @click="onAddPackage">
          Create New Work Instruction Package
        </b-button>
      </div>
    </div>

    <CreateStandardWorkInstructionModal
      v-if="showCreateModal"
      create-title="Create New Work Instruction Package"
      @close="showCreateModal = false"
      @save="onSaveNewPackage"
    />

    <CreateStandardWorkInstructionModal
      v-if="showEditModal"
      :initial-data="editingRow"
      @close="showEditModal = false"
      @save="onSaveEdit"
    />

    <div class="items">
      <div class="items-container">
        <b-table
          :data="filteredRows"
          :hoverable="true"
          :loading="isLoading"
          paginated
          :pagination-simple="true"
          :per-page="10"
          default-sort="name"
          @click="openPackage"
        >
          <template slot-scope="props">
            <b-table-column
              v-for="column in columns"
              :key="column.field"
              :field="column.field"
              :label="column.label"
              :searchable="column.searchable"
            >
              {{ props.row[column.field] }}
            </b-table-column>

            <b-table-column
              field="favorite"
              :label="$t('work_instructions.table.favorite')"
            >
              <span class="favorite-cell">
                <b-icon icon="star" :class="{ active: props.row.isFavorite }" />
                {{ props.row.favoriteCount }}
              </span>
            </b-table-column>

            <b-table-column custom-key="actions" class="options-column">
              <div @click.stop>
                <b-dropdown
                  aria-role="list"
                  :position="props.index > 1 ? 'is-top-left' : 'is-bottom-left'"
                >
                  <button class="button options-more-btn" slot="trigger">
                    <b-icon icon="dots-horizontal"></b-icon>
                  </button>

                  <b-dropdown-item
                    aria-role="listitem"
                    @click="openEdit(props.row)"
                  >
                    <div class="media">
                      <b-icon class="media-left" icon="pencil"></b-icon>
                      <div class="media-content">
                        <h3>{{ $t('edit') }}</h3>
                      </div>
                    </div>
                  </b-dropdown-item>

                  <b-dropdown-item
                    aria-role="listitem"
                    @click="confirmDelete(props.row)"
                  >
                    <div class="media">
                      <b-icon class="media-left" icon="delete"></b-icon>
                      <div class="media-content">
                        <h3>{{ $t('delete') }}</h3>
                      </div>
                    </div>
                  </b-dropdown-item>
                </b-dropdown>
              </div>
            </b-table-column>
          </template>

          <template #empty>
            <section class="section wi-empty-state">
              <div class="content has-text-grey has-text-centered">
                <p>No instruction packages found.</p>
              </div>
            </section>
          </template>
        </b-table>
      </div>
    </div>
  </div>
</template>

<script>
import { v4 } from 'uuid';
import client from '../../config/api';
import EditorModuleTabs from './ModuleTabs';
import CreateStandardWorkInstructionModal from '../Shared/WorkInstructions/CreateStandardWorkInstructionModal';
import instructionPackagesColumns from './instructionPackages/columns';

const uuid = v4;

export default {
  name: 'editor-instruction-packages-list',
  components: {
    EditorModuleTabs,
    CreateStandardWorkInstructionModal,
  },
  data() {
    return {
      isLoading: false,
      search: '',
      rows: [],
      columns: instructionPackagesColumns,
      showCreateModal: false,
      showEditModal: false,
      editingRow: null,
    };
  },
  computed: {
    filteredRows() {
      const term = (this.search || '').toLowerCase().trim();
      if (!term) return this.rows;

      return this.rows.filter((row) =>
        [
          row.name,
          row.description,
          row.status,
          row.productLine,
          row.projectPhase,
          row.oem,
          row.linkedTemplates,
          row.linkedInstructions,
          row.linkedTasks,
          row.date,
        ]
          .join(' ')
          .toLowerCase()
          .includes(term)
      );
    },
  },
  async created() {
    await this.fetchRows();
  },
  methods: {
    isWorkInstructionType(wiPackage) {
      const type = (wiPackage?.type || '').toString().trim();
      return type === 'standard_instruction_package';
    },

    normalizeSelectionValue(value) {
      if (Array.isArray(value)) {
        return this.normalizeSelectionValue(value[0]);
      }

      if (value && typeof value === 'object') {
        return (
          value.name ||
          value.text ||
          value.value ||
          value.label ||
          ''
        ).toString();
      }

      return value == null ? '' : value.toString();
    },

    getFirstNormalizedValue(...values) {
      return (
        values
          .map((value) => this.normalizeSelectionValue(value).trim())
          .find((normalized) => Boolean(normalized)) || '-'
      );
    },

    getLinkedCount(value, fallback = '-') {
      if (Array.isArray(value)) {
        return value.length;
      }

      if (typeof value === 'number') {
        return value;
      }

      return fallback;
    },

    isUuidLike(value) {
      if (!value) return false;
      return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
        String(value).trim()
      );
    },

    resolveRoutePackageId(wiPackage) {
      const candidates = [
        wiPackage?.uuid,
        wiPackage?.id,
        wiPackage?.packageId,
        wiPackage?._id,
      ]
        .map((value) => (value == null ? '' : String(value).trim()))
        .filter(Boolean);

      return (
        candidates.find((candidate) => this.isUuidLike(candidate)) ||
        candidates[0] ||
        null
      );
    },

    formatDate(dateStr) {
      if (!dateStr) return '-';
      const d = new Date(dateStr);
      if (isNaN(d)) return '-';
      return `${d.getMonth() + 1}-${d.getDate()}-${d.getFullYear()}`;
    },

    async fetchRows() {
      this.isLoading = true;
      try {
        const response = await client.get('/work-instructions-package');
        const packages = response?.data || [];

        this.rows = packages
          .filter(
            (wiPackage) => wiPackage.type === 'standard_instruction_package'
          )
          .map((wiPackage) => ({
            id: wiPackage._id,
            routeId: this.resolveRoutePackageId(wiPackage),
            name: wiPackage.title || '-',
            description: wiPackage.description || wiPackage.category || '-',
            status: wiPackage.status || 'Draft',
            productLine: this.getFirstNormalizedValue(
              wiPackage.productLine,
              wiPackage.ProductLine
            ),
            projectPhase: this.getFirstNormalizedValue(
              wiPackage.projectPhase,
              wiPackage.ProjectPhase
            ),
            oem: this.getFirstNormalizedValue(
              wiPackage.originalOEM,
              wiPackage.OriginalOEM,
              wiPackage.originalOem,
              wiPackage.originalOEMBrand
            ),
            linkedTemplates: this.getLinkedCount(
              wiPackage.linkedTemplates,
              '...'
            ),
            linkedInstructions: this.getLinkedCount(
              wiPackage.workInstructions,
              0
            ),
            linkedTasks: this.getLinkedCount(wiPackage.linkedTasks, '...'),
            date: this.formatDate(wiPackage.createdAt),
            favoriteCount: wiPackage.favoriteCount || 0,
            isFavorite: false,
          }));
      } finally {
        this.isLoading = false;
      }
    },

    onAddPackage() {
      this.showCreateModal = true;
    },

    openEdit(row) {
      this.editingRow = {
        id: row.id,
        title: row.name,
        description: row.description === '-' ? '' : row.description,
        status: row.status,
        productLine: row.productLine,
        projectPhase: row.projectPhase,
        originalOEM: row.oem,
      };
      this.showEditModal = true;
    },

    confirmDelete(row) {
      this.$buefy.dialog.confirm({
        title: this.$t('delete'),
        message: `Are you sure you want to delete <strong>${row.name}</strong>? This action cannot be undone.`,
        confirmText: this.$t('delete'),
        type: 'is-danger',
        hasIcon: true,
        icon: 'delete',
        onConfirm: () => this.deleteRow(row),
      });
    },

    async deleteRow(row) {
      await client.delete(`/work-instructions-package/${row.id}`);
      await this.fetchRows();
    },

    async onSaveEdit(payload) {
      this.showEditModal = false;
      if (!this.editingRow?.id) return;

      await client.put(`/work-instructions-package/${this.editingRow.id}`, {
        title: payload.title,
        description: payload.description,
        type: 'standard_instruction_package',
        status: payload.status || 'Draft',
        productLine: payload.productLine,
        projectPhase: payload.projectPhase,
        originalOEM: payload.originalOEM,
      });

      await this.fetchRows();
    },

    async onSaveNewPackage(payload) {
      this.showCreateModal = false;

      const newPackage = {
        _id: uuid(),
        createdAt: new Date().toISOString(),
        isStandard: true,
        type: 'standard_instruction_package',
        jobId: 'standard',
        sortOrder: this.rows.length,
        workInstructions: [],
        status: payload.status || 'Draft',
        owner: this.$store.state.currentUser?.fullname || '',
        favoriteCount: 0,
        ...payload,
      };

      await client.post('/work-instructions-package', newPackage);
      await this.fetchRows();
    },

    openPackage(row) {
      const routePackageId = row?.routeId || row?.id;
      if (!routePackageId) return;

      this.$router.push({
        name: 'editor-instruction-packages-detail',
        params: { packageId: routePackageId },
      });
    },
  },
};
</script>

<style scoped lang="scss">
.header {
  font-family: 'Siemens Sans';
  display: flex;
  align-items: stretch;
  justify-content: space-between;
  width: 100%;
  height: 50px;
  background-color: #fafafa;
  font-size: 18px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.4);
  color: #333;
  padding-left: 20px;

  .list-title {
    flex: 1 1 auto;
    min-width: 0;
    height: 50px;
    line-height: 50px;
    padding: 0 10px;
  }

  .right-section {
    flex: 0 0 auto;
    display: flex;
    align-items: stretch;

    .search {
      height: 100%;
      padding: 5px 16px;
      background-color: #e0e0e0;

      :deep(.field) {
        margin-bottom: 0;
      }

      :deep(.search-input) {
        width: 230px;
      }

      .search-input input {
        border: 0 !important;
        background-color: #e0e0e0 !important;
        outline: none;
        box-shadow: none;
      }
    }
  }
}

.items {
  height: calc(100vh - 100px);
  overflow-y: auto;

  .items-container {
    padding: 45px 30px 10px 30px;
  }
}

.wi-add-button.button {
  border: none;
  border-radius: 0;
  height: 50px;
  min-width: 170px;
  padding: 0 18px;
  margin: 0;
  background: #4a78d0;
  color: #fff;
  font-family: 'Siemens Sans';
  font-size: 16px;
  font-weight: 600;
  white-space: nowrap;
}

.wi-add-button.button:hover,
.wi-add-button.button:focus {
  color: #fff;
  background: #3e6ec7;
}

.favorite-cell {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.favorite-cell .icon {
  color: #c6c6c6;
}

.favorite-cell .icon.active {
  color: #f6c343;
}

.options-column {
  cursor: default;
  min-width: 90px;

  .options-more-btn {
    border: none;
    background: transparent;
    box-shadow: none;
    color: #666;

    &:hover {
      color: #333;
    }

    &:focus {
      box-shadow: none;
    }
  }
}

.wi-empty-state {
  padding: 2rem 1rem;
}

:deep(.table tbody tr) {
  cursor: pointer;
}
</style>
